/**
 * Copy deck - Editorial Authority landing (`/` EN + `/vi/` VN).
 * Spec: docs/superpowers/specs/2026-06-11-asklaw-editorial-authority-design-system.md sec 5 + 13b.1.
 * MOI string VN PHAI du dau day du (guard check-vn-copy.mjs).
 * So lieu stat verified tu DB_TW + DB_DP 2026-06-11: TW 55,462 + DP 104,626 = 160,088 doc;
 * refs 198,349 + 604,684 = 803,033; 34 tinh thanh.
 * Wording accuracy: "target + methodology" per spec sec 10 (F14) - data-quality gate chot.
 */

import type { Lang } from '../use-lang';

export type LandingCopy = {
  meta_title: string;
  meta_description: string;
  hero_eyebrow: string;
  hero_headline: string;
  hero_lead: string;
  hero_cta_primary: string;
  hero_cta_secondary: string;
  stats: { num: string; label: string }[];
  stats_note: string;
  how_eyebrow: string;
  how_heading: string;
  how_steps: { title: string; body: string }[];
  features_eyebrow: string;
  features_heading: string;
  features: { title: string; body: string; to: string; cta: string }[];
  audience_eyebrow: string;
  audience_heading: string;
  audiences: { title: string; body: string; to: string; cta: string }[];
  trust_tier_eyebrow: string;
  trust_tier_heading: string;
  trust_tier_points: string[];
  pricing_heading: string;
  pricing_cta: string;
  footer_cols: { heading: string; links: { label: string; to: string }[] }[];
};

const EN: LandingCopy = {
  meta_title: 'Asklaw - Understand Vietnamese law with verifiable citations',
  meta_description:
    'AI legal assistant for Vietnamese law. Plain-language answers citing every Article and Clause from the official VBPL corpus - 160,000+ documents from central government and all 34 provinces, updated daily.',
  hero_eyebrow: 'Vietnamese law, clear in English',
  hero_headline: 'Understand Vietnamese law with verifiable citations.',
  hero_lead:
    'Asklaw answers in plain language and cites every Article and Clause from Vietnam\'s official legal corpus - so you can verify everything on the government source.',
  hero_cta_primary: 'Start asking',
  hero_cta_secondary: 'Search documents',
  stats: [
    { num: '160,000+', label: 'Legal documents searchable' },
    { num: '34', label: 'Provinces covered, plus central government' },
    { num: '800,000+', label: 'Citation links mapped' },
    { num: '95%+', label: 'Citation accuracy target, methodology published' },
  ],
  stats_note: 'Synced daily from vbpl.moj.gov.vn - the official legal database of the Ministry of Justice.',
  how_eyebrow: 'How Asklaw works',
  how_heading: 'Answers you can stand behind',
  how_steps: [
    { title: 'Ask in plain language', body: 'Type your question in English or Vietnamese - no legal jargon needed.' },
    { title: 'Asklaw retrieves official provisions', body: 'The engine searches the official VBPL corpus and ranks the governing documents.' },
    { title: 'Verify every citation', body: 'Each answer cites Articles and Clauses side by side, linked to the official text.' },
  ],
  features_eyebrow: 'The full product',
  features_heading: 'Everything you need to navigate Vietnamese law',
  features: [
    { title: 'Ask AI', body: 'Q&A with Article/Clause citations you can verify.', to: '/chat?from=landing-editorial', cta: 'Open chat' },
    { title: 'Search documents', body: 'Search 160,000+ documents from central and all 34 provinces.', to: '/tim-kiem', cta: 'Search now' },
    { title: 'Legal news', body: 'New documents and effective-date changes, tracked daily.', to: '/news', cta: 'Read news' },
    { title: 'Shortcuts', body: 'Ready-made flows for common legal scenarios.', to: '/shortcut', cta: 'See shortcuts' },
    { title: 'Doc Pack', body: 'Curated document bundles by topic, with usage notes.', to: '/docpack', cta: 'See packs' },
  ],
  audience_eyebrow: 'Built for',
  audience_heading: 'Whoever you are in Vietnam, the law applies to you',
  audiences: [
    { title: 'Expats and foreign investors', body: 'Work permits, leases, visas, company setup - in English, with the legal source attached.', to: '/chat?from=landing-audience-expat', cta: 'Ask your question' },
    { title: 'Individuals', body: 'Labor, land, marriage and family - understand your rights before you sign.', to: '/chat?from=landing-audience-individual', cta: 'Ask your question' },
    { title: 'Businesses', body: 'Compliance, contracts, tax and licensing - grounded on central legislation, with the source attached.', to: '/chat?from=landing-audience-business', cta: 'Ask your question' },
    { title: 'Legal professionals', body: 'Fast retrieval across 160,000+ documents with citation graphs.', to: '/tim-kiem', cta: 'Search the corpus' },
  ],
  trust_tier_eyebrow: 'Citation-Grade AI',
  trust_tier_heading: 'Not "AI-powered". Citation-grade.',
  trust_tier_points: [
    'Grounded only on the official VBPL corpus - no open-web guessing.',
    'Every answer cites Articles and Clauses you can verify at vbpl.moj.gov.vn.',
    'Refuses to answer when the law is not clear, instead of guessing.',
    'AI-generated output is labeled, with audit logs per Vietnam AI Act 2026.',
  ],
  pricing_heading: 'Ask for free. Doc Pack from $19. Pro tier coming soon.',
  pricing_cta: 'Start free',
  footer_cols: [
    { heading: 'Product', links: [
      { label: 'Ask AI', to: '/chat' }, { label: 'Search documents', to: '/tim-kiem' },
      { label: 'Legal news', to: '/news' }, { label: 'Shortcuts', to: '/shortcut' }, { label: 'Doc Pack', to: '/docpack' },
    ] },
    { heading: 'Company', links: [
      { label: 'About', to: '/about' }, { label: 'Editorial standards', to: '/editorial-standards' },
      { label: 'Transparency', to: '/transparency' }, { label: 'Contact', to: '/contact' },
    ] },
    { heading: 'Legal', links: [
      { label: 'Privacy', to: '/privacy' }, { label: 'Terms', to: '/terms' }, { label: 'Report an issue', to: '/report-issue' },
    ] },
    { heading: 'Language', links: [
      { label: 'Tieng Viet', to: '/vi/' }, { label: 'English', to: '/' },
    ] },
  ],
};

const VI: LandingCopy = {
  meta_title: 'Asklaw - Hiểu pháp luật Việt Nam với trích dẫn kiểm chứng được',
  meta_description:
    'Trợ lý pháp lý AI cho pháp luật Việt Nam. Trả lời dễ hiểu, trích dẫn từng Điều, Khoản từ kho VBPL chính thức - hơn 160.000 văn bản Trung ương và 34 tỉnh thành, cập nhật hằng ngày.',
  hero_eyebrow: 'Pháp luật Việt Nam, rõ ràng và có căn cứ',
  hero_headline: 'Hiểu pháp luật Việt Nam với trích dẫn kiểm chứng được.',
  hero_lead:
    'Asklaw trả lời bằng ngôn ngữ dễ hiểu và trích dẫn từng Điều, Khoản từ kho văn bản pháp luật chính thức - bạn có thể kiểm tra mọi trích dẫn trên nguồn của nhà nước.',
  hero_cta_primary: 'Bắt đầu hỏi',
  hero_cta_secondary: 'Tìm văn bản',
  stats: [
    { num: '160,000+', label: 'Văn bản pháp luật tra cứu được' },
    { num: '34', label: 'Tỉnh thành, cùng văn bản Trung ương' },
    { num: '800,000+', label: 'Liên kết trích dẫn' },
    { num: '95%+', label: 'Mục tiêu độ chính xác trích dẫn, phương pháp công khai' },
  ],
  stats_note: 'Đồng bộ hằng ngày từ vbpl.moj.gov.vn - cơ sở dữ liệu pháp luật chính thức của Bộ Tư pháp.',
  how_eyebrow: 'Asklaw hoạt động thế nào',
  how_heading: 'Câu trả lời bạn có thể tin cậy',
  how_steps: [
    { title: 'Hỏi bằng ngôn ngữ thường', body: 'Gõ câu hỏi bằng tiếng Việt hoặc tiếng Anh - không cần thuật ngữ pháp lý.' },
    { title: 'Asklaw truy xuất quy định chính thức', body: 'Hệ thống tìm trong kho VBPL chính thức và xếp hạng các văn bản điều chỉnh.' },
    { title: 'Kiểm chứng từng trích dẫn', body: 'Mỗi câu trả lời trích dẫn Điều, Khoản song song, liên kết tới văn bản gốc.' },
  ],
  features_eyebrow: 'Sản phẩm đầy đủ',
  features_heading: 'Mọi thứ bạn cần để nắm pháp luật Việt Nam',
  features: [
    { title: 'Hỏi AI', body: 'Hỏi đáp kèm trích dẫn Điều/Khoản kiểm chứng được.', to: '/chat?from=landing-editorial', cta: 'Mở Chat' },
    { title: 'Tra cứu văn bản', body: 'Tìm trong hơn 160.000 văn bản Trung ương và 34 tỉnh thành.', to: '/tim-kiem', cta: 'Tra cứu ngay' },
    { title: 'Tin pháp luật', body: 'Văn bản mới và thay đổi hiệu lực, theo dõi hằng ngày.', to: '/news', cta: 'Đọc tin' },
    { title: 'Shortcut', body: 'Kịch bản soạn sẵn cho tình huống pháp lý thường gặp.', to: '/shortcut', cta: 'Xem Shortcut' },
    { title: 'Doc Pack', body: 'Gói văn bản chọn lọc theo chuyên đề, kèm hướng dẫn áp dụng.', to: '/docpack', cta: 'Xem gói' },
  ],
  audience_eyebrow: 'Dành cho',
  audience_heading: 'Dù bạn là ai ở Việt Nam, pháp luật đều liên quan đến bạn',
  audiences: [
    { title: 'Người nước ngoài và nhà đầu tư', body: 'Giấy phép lao động, thuê nhà, visa, lập công ty - bằng tiếng Anh, kèm căn cứ pháp lý.', to: '/chat?from=landing-audience-expat', cta: 'Đặt câu hỏi' },
    { title: 'Cá nhân', body: 'Lao động, đất đai, hôn nhân gia đình - hiểu quyền của bạn trước khi ký.', to: '/chat?from=landing-audience-individual', cta: 'Đặt câu hỏi' },
    { title: 'Doanh nghiệp', body: 'Tuân thủ, hợp đồng, thuế và giấy phép - căn cứ trên văn bản Trung ương, kèm nguồn pháp lý.', to: '/chat?from=landing-audience-business', cta: 'Đặt câu hỏi' },
    { title: 'Người hành nghề luật', body: 'Truy xuất nhanh trên hơn 160.000 văn bản với đồ thị trích dẫn.', to: '/tim-kiem', cta: 'Tra cứu kho văn bản' },
  ],
  trust_tier_eyebrow: 'AI chuẩn trích dẫn',
  trust_tier_heading: 'Không chỉ là "AI". Là AI chuẩn trích dẫn.',
  trust_tier_points: [
    'Chỉ dựa trên kho VBPL chính thức - không đoán từ web mở.',
    'Mỗi câu trả lời trích dẫn Điều, Khoản kiểm chứng được tại vbpl.moj.gov.vn.',
    'Từ chối trả lời khi quy định chưa rõ, thay vì đoán.',
    'Nội dung do AI tạo được gắn nhãn, có nhật ký kiểm toán theo Luật Trí tuệ nhân tạo 2026.',
  ],
  pricing_heading: 'Hỏi miễn phí. Doc Pack từ 199.000đ. Gói Pro sắp ra mắt.',
  pricing_cta: 'Bắt đầu miễn phí',
  footer_cols: [
    { heading: 'Sản phẩm', links: [
      { label: 'Hỏi AI', to: '/chat' }, { label: 'Tra cứu pháp luật', to: '/tim-kiem' },
      { label: 'Tin pháp luật', to: '/news' }, { label: 'Shortcut', to: '/shortcut' }, { label: 'Doc Pack', to: '/docpack' },
    ] },
    { heading: 'Công ty', links: [
      { label: 'Về Asklaw', to: '/about' }, { label: 'Tiêu chuẩn biên tập', to: '/editorial-standards' },
      { label: 'Minh bạch', to: '/transparency' }, { label: 'Liên hệ', to: '/contact' },
    ] },
    { heading: 'Pháp lý', links: [
      { label: 'Bảo mật', to: '/privacy' }, { label: 'Điều khoản', to: '/terms' }, { label: 'Báo lỗi nội dung', to: '/report-issue' },
    ] },
    { heading: 'Ngôn ngữ', links: [
      { label: 'Tiếng Việt', to: '/vi/' }, { label: 'English', to: '/' },
    ] },
  ],
};

export function getLandingEditorialCopy(lang: Lang): LandingCopy {
  return lang === 'vi' ? VI : EN;
}
